import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { toast } from "react-toastify";
import CustomToast from "src/components/CustomToast/CustomToast";
import cmsClient from "src/services/cmsClient";

const sendAnnouncement = async (id: number): Promise<void> => {
	await cmsClient.post(`/announcements/${id}/send/`);
};

const useSendAnnouncement = () => {
	const queryClient = useQueryClient();

	const onSuccess = () => {
		toast.success(
			<CustomToast type="Success" message="Announcement sent successfully" />,
			{ hideProgressBar: true }
		);
	};

	const onSettled = () => {
		queryClient.invalidateQueries(["announcements"]);
	};

	const onError = (error: unknown) => {
		const axiosError = error as AxiosError<{ detail?: string }>;
		// The backend returns 409 with `{ detail: "..." }` for any
		// non-success ServiceResult — including the video-processing
		// gate ("videos are still being processed"), the
		// already-sent guard ("Only draft announcements can be sent"),
		// and audience validation. Surface the server's message
		// directly so the author can act on it instead of seeing a
		// generic "already been sent" that's often wrong.
		const detail = axiosError?.response?.data?.detail;
		const status = axiosError?.response?.status;
		const message =
			detail ??
			(status === 409
				? "This announcement could not be sent right now. Try again in a moment."
				: "Something went wrong, try again");
		toast.error(<CustomToast type="Error" message={message} />, {
			hideProgressBar: true
		});
	};

	const mutation = useMutation(sendAnnouncement, {
		onError,
		onSuccess,
		onSettled
	});
	return mutation;
};

export default useSendAnnouncement;
